"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.NotesTextArea = exports.EditableNote = void 0;
const jsx_runtime_1 = require("@emotion/react/jsx-runtime");
const react_1 = require("react");
const get_string_length_1 = require("../../util/get_string_length");
const styles_1 = require("../../styles");
const typography_1 = require("../typography");
const EditableNote = ({ noteText, placeholderText, description, onSave, characterLimit, disableEditing, errorMessage, hasFootBar = true, testId, showChrome = true, isEditing, showTitle = true, notesChromeStyles = {}, }) => {
    const textareaRef = (0, react_1.useRef)(null);
    const [note, setNote] = (0, react_1.useState)(noteText);
    const [noteDraft, setNoteDraft] = (0, react_1.useState)(noteText);
    const noteLimitReached = !!noteDraft && noteDraft.length >= characterLimit;
    const [edit, setEdit] = (0, react_1.useState)(false);
    const [showSavedMessage, setShowSavedMessage] = (0, react_1.useState)(false);
    const debounceSave = (0, react_1.useCallback)(() => {
        setTimeout(() => {
            const updatedNote = noteDraft ? noteDraft.trim() : '';
            if (updatedNote !== note)
                setShowSavedMessage(true);
            setNote(updatedNote);
            onSave(updatedNote);
        }, 500);
    }, [note, noteDraft, onSave]);
    const resizeTextarea = (0, react_1.useCallback)(() => {
        requestAnimationFrame(() => {
            if (textareaRef.current) {
                textareaRef.current.style.height = '5px';
                textareaRef.current.style.height = `${textareaRef.current.scrollHeight + 8}px`;
            }
        });
    }, []);
    const stopEdit = (0, react_1.useCallback)(() => {
        setEdit(false);
        resizeTextarea();
    }, [setEdit, resizeTextarea]);
    const handleFocusLost = (0, react_1.useCallback)(() => {
        stopEdit();
    }, [stopEdit]);
    const startEdit = (0, react_1.useCallback)(() => {
        setEdit(true);
        setNoteDraft(note);
        textareaRef.current.focus();
        // sets the cursor to the end of the text when there is text in the textarea
        const textLength = textareaRef.current.value.length;
        textareaRef.current.setSelectionRange(textLength, textLength);
        resizeTextarea();
    }, [note, resizeTextarea]);
    const handleFocus = (0, react_1.useCallback)(() => {
        if (edit) {
            return;
        }
        startEdit();
    }, [edit, startEdit]);
    const handleKeyUp = (0, react_1.useCallback)((e) => {
        if (e.key === 'Escape') {
            handleFocusLost();
            setNoteDraft(note);
            textareaRef.current.blur();
        }
    }, [handleFocusLost, note]);
    const handleBlur = () => {
        debounceSave();
        handleFocusLost();
    };
    const renderCharCount = noteDraft ? `${(0, get_string_length_1.getStringLength)(noteDraft)}/${characterLimit}` : '';
    const handleInputChange = (0, react_1.useCallback)((e) => {
        let draft = e.target.value;
        if (noteLimitReached) {
            draft = draft.substring(0, characterLimit);
        }
        setNoteDraft(draft);
    }, [characterLimit, noteLimitReached]);
    (0, react_1.useEffect)(() => {
        resizeTextarea();
    }, [note, noteDraft, resizeTextarea]);
    (0, react_1.useEffect)(() => {
        setNote(noteText);
        setNoteDraft(noteText);
    }, [noteText]);
    (0, react_1.useEffect)(() => {
        if (!edit && textareaRef.current.scrollTop !== 0) {
            textareaRef.current.scrollTop = 0;
        }
    }, [edit, noteDraft]);
    (0, react_1.useEffect)(() => {
        if (isEditing) {
            startEdit();
        }
    }, [isEditing, startEdit]);
    return ((0, jsx_runtime_1.jsxs)(NoteContainer, Object.assign({ "data-testid": testId }, { children: [(0, jsx_runtime_1.jsx)(EditableNotesHeader, { description: description, errorMessage: errorMessage, edit: edit, renderCharCount: renderCharCount, showSavedMessage: showSavedMessage, noteLimitReached: noteLimitReached, showTitle: showTitle, notesChromeStyles: notesChromeStyles, showChrome: showChrome }), (0, jsx_runtime_1.jsx)(exports.NotesTextArea, { ref: textareaRef, placeholderText: disableEditing ? 'No call notes' : placeholderText, characterLimit: characterLimit, initialNote: note, currentNote: noteDraft, isEditingYn: edit, isEditingDisabledYn: disableEditing, handleKeyUp: handleKeyUp, handleInputChange: handleInputChange, handleFocus: handleFocus, handleBlur: handleBlur, errorMessage: errorMessage, isBrowserChromeYn: showChrome, showBottomBorderYn: hasFootBar })] })));
};
exports.EditableNote = EditableNote;
exports.EditableNote.displayName = 'EditableNote';
const EditableNotesHeader = ({ showTitle, description, errorMessage, showSavedMessage, edit, renderCharCount, showChrome = true, notesChromeStyles, noteLimitReached, }) => {
    return ((0, jsx_runtime_1.jsxs)(NotesChrome, Object.assign({ showChrome: showChrome, notesChromeStyles: notesChromeStyles }, { children: [showTitle && ((0, jsx_runtime_1.jsx)(typography_1.Subheader, Object.assign({ id: "call-notes-heading", "data-testid": "call-notes-heading", size: "mediumLarge" }, { children: description }))), (0, jsx_runtime_1.jsxs)(MessageContainer, Object.assign({ description: description }, { children: [!!errorMessage && ((0, jsx_runtime_1.jsx)(typography_1.AppText, Object.assign({ "data-testid": "notes-error-message", color: "red50" }, { children: errorMessage }))), showSavedMessage && !edit && !errorMessage && ((0, jsx_runtime_1.jsx)(typography_1.AppText, Object.assign({ color: "medium" }, { children: "Saved!" }))), (showChrome || edit || !!errorMessage) && ((0, jsx_runtime_1.jsx)(CharCounter, Object.assign({ atCharLimit: noteLimitReached }, { children: renderCharCount })))] }))] })));
};
exports.NotesTextArea = (0, react_1.forwardRef)(({ placeholderText, characterLimit, handleKeyUp, isEditingYn, isEditingDisabledYn, initialNote, currentNote, handleInputChange, handleFocus, handleBlur, errorMessage, isBrowserChromeYn, showBottomBorderYn, }, textareaRef) => {
    const value = isEditingYn ? currentNote : initialNote;
    return ((0, jsx_runtime_1.jsx)(NoteTextArea, { "aria-labelledby": "call-notes-heading", "data-testid": "call-note-textarea-input", ref: textareaRef, onKeyUp: handleKeyUp, value: value, onChange: handleInputChange, onFocus: handleFocus, onBlur: handleBlur, isEditing: isEditingYn, placeholder: isEditingDisabledYn ? 'No call notes' : placeholderText, maxLength: characterLimit, disabled: isEditingDisabledYn, error: !!errorMessage, hasFootBar: showBottomBorderYn, showChrome: isBrowserChromeYn, className: isEditingYn ? 'focus-visible' : '' }));
});
exports.NotesTextArea.displayName = 'NotesTextArea';
const CharCounter = styles_1.styled.span(({ theme, atCharLimit }) => ({
    fontSize: theme.text.size.medium,
    fontFamily: theme.text.bodyFontFamily,
    fontWeight: 400,
    flexGrow: 1,
    textAlign: 'right',
    color: atCharLimit ? theme.colors.red50 : theme.colors.grey60,
    paddingLeft: theme.spacing.s,
}));
const NoteContainer = styles_1.styled.div(() => ({
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
}));
const NoteTextArea = styles_1.styled.textarea(({ theme, isEditing, error, hasFootBar, showChrome, disabled }) => ({
    minHeight: '4.5rem',
    maxHeight: '15.875rem',
    padding: showChrome
        ? '4px 6px !important'
        : isEditing || error
            ? `${theme.spacing.xxs} ${theme.spacing.xs}`
            : `${theme.spacing.xxs} 0`,
    marginTop: theme.spacing.xxs,
    flexGrow: 1,
    fontSize: theme.text.size.medium,
    fontFamily: theme.text.bodyFontFamily,
    backgroundColor: isEditing ? theme.colors.white : theme.colors.transparent,
    outline: 'none',
    borderRadius: theme.radii[1],
    resize: 'none',
    overflowY: 'auto',
    border: isEditing
        ? `1px solid ${theme.colors.transparent}`
        : error
            ? `1px solid ${theme.colors.red50}`
            : showChrome
                ? `1px solid ${theme.colors.grey10}`
                : `1px solid ${theme.colors.transparent}`,
    borderBottom: hasFootBar && !isEditing && !error && !showChrome
        ? `1px solid ${theme.colors.grey10}`
        : null,
    '.focus-visible.focus-visible': isEditing !== null && isEditing !== void 0 ? isEditing : {
        boxShadow: 'none',
    },
    ':disabled': {
        color: '#000000',
    },
    ':hover': {
        backgroundColor: !isEditing && !disabled ? theme.colors.lightBlue : theme.colors.white,
        cursor: disabled ? 'not-allowed' : 'auto',
    },
    ':focus': {
        boxShadow: '2px blue',
    },
}));
const NotesChrome = styles_1.styled.div(({ showChrome, theme, notesChromeStyles = {} }) => (Object.assign({ display: 'flex', justifyContent: 'space-between', minHeight: '1.5rem', marginBottom: showChrome ? theme.spacing.xs : 0 }, notesChromeStyles)));
const MessageContainer = styles_1.styled.div(({ description }) => ({
    display: 'flex',
    justifyContent: description ? 'flex-end' : 'flex-start',
    alignItems: 'center',
}));
//# sourceMappingURL=editable_note.js.map